import { useEffect, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";

import * as auth from "@/script/auth";
import {
  clearDemoSsoContextIfMatches,
  readDemoSsoContext,
} from "@/script/demo-sso-session";
import useStore from "@/store/store";

const DEMO_SESSION_RESTORE_TIMEOUT_MS = 5000;

export function useDemoSessionRestore(): boolean {
  const {
    isAuthenticated,
    loginUser,
    demoSession,
    setActiveDemoRole,
    setDemoSession,
  } = useStore(
    useShallow((state) => ({
      isAuthenticated: state.isAuthenticated,
      loginUser: state.loginUser,
      demoSession: state.demoSession,
      setActiveDemoRole: state.setActiveDemoRole,
      setDemoSession: state.setDemoSession,
    }))
  );
  const [isRestoring, setIsRestoring] = useState(() => {
    const context = readDemoSsoContext();
    return isAuthenticated && context?.phase === "authenticated";
  });
  const restoreInFlightRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsRestoring(false);
      return;
    }
    if (!loginUser) return;
    if (demoSession) {
      setIsRestoring(false);
      return;
    }
    if (restoreInFlightRef.current) return;

    const context = readDemoSsoContext();
    if (!context || context.phase !== "authenticated") {
      setIsRestoring(false);
      return;
    }

    restoreInFlightRef.current = true;
    setIsRestoring(true);
    let isCurrent = true;
    const controller = new AbortController();
    const timer = window.setTimeout(
      () => controller.abort(),
      DEMO_SESSION_RESTORE_TIMEOUT_MS
    );

    const restoreDemoSession = async (): Promise<void> => {
      try {
        const token = await auth.fetchTokenFromMeditong(
          context.memberid,
          controller.signal
        );
        const mode = await auth.fetchSsoMode(token, controller.signal);
        if (!isCurrent) return;
        if (mode.mode !== "demo" || mode.roles.length === 0) {
          clearDemoSsoContextIfMatches(context);
          return;
        }

        const currentContext = readDemoSsoContext();
        if (
          !currentContext ||
          currentContext.phase !== "authenticated" ||
          currentContext.memberid !== context.memberid
        ) {
          return;
        }
        if (
          currentContext.activeRole !== null &&
          !mode.roles.some((role) => role.role === currentContext.activeRole)
        ) {
          clearDemoSsoContextIfMatches(context);
          return;
        }

        setDemoSession({
          token: token.token,
          memberid: token.memberid,
          roles: mode.roles,
        });
        setActiveDemoRole(currentContext.activeRole);
      } catch (error) {
        if (isCurrent) clearDemoSsoContextIfMatches(context);
        if (error instanceof auth.AuthError) return;
        if (error instanceof DOMException && error.name === "AbortError") return;
        throw error;
      } finally {
        window.clearTimeout(timer);
        if (isCurrent) {
          restoreInFlightRef.current = false;
          setIsRestoring(false);
        }
      }
    };

    void restoreDemoSession();
    return () => {
      isCurrent = false;
      controller.abort();
      window.clearTimeout(timer);
      restoreInFlightRef.current = false;
    };
  }, [
    demoSession,
    isAuthenticated,
    loginUser,
    setActiveDemoRole,
    setDemoSession,
  ]);

  return isRestoring;
}
